import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm,
  Card,
  Typography,
  Tabs,
  Switch,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined
} from '@ant-design/icons';
import apiService from '../../services/api';
import RepresentativePaperManagement from './RepresentativePaperManagement';

const { Title } = Typography;
const { TextArea } = Input;

interface ResearchArea {
  _id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  details: string[];
  isActive: boolean;
}
interface Equipment {
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
}

interface ResearchContent {
  researchAreas: ResearchArea[];
  equipment: Equipment[];
}

const ResearchManagement: React.FC = () => {
  const [researchContent, setResearchContent] = useState<ResearchContent>({
    researchAreas: [],
    equipment: []
  });
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState<'area' | 'equipment'>('area');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchResearchContent();
  }, []);

  const fetchResearchContent = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<ResearchContent>('/research-content/admin');
      setResearchContent(response.data || {
        researchAreas: [],
        equipment: []
      });
    } catch (error) {
      message.error('获取研究成果内容失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (type: 'area' | 'equipment') => {
    setModalType(type);
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (item: any, type: 'area' | 'equipment') => {
    setModalType(type);
    setEditingItem(item);
    
    // 对于研究方向，需要特殊处理details字段
    if (type === 'area' && item.details) {
      const formValues = {
        ...item,
        details: item.details.join('\n') // 将数组转换为换行分隔的字符串
      };
      form.setFieldsValue(formValues);
    } else {
      form.setFieldsValue(item);
    }
    
    setModalVisible(true);
  };

  const handleDelete = async (id: string, type: 'area' | 'equipment') => {
    try {
      await apiService.delete(`/research-content/${type}/${id}`);
      message.success('删除成功');
      fetchResearchContent();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingItem) {
        await apiService.put(`/research-content/${modalType}/${editingItem._id}`, values);
        message.success('更新成功');
      } else {
        await apiService.post(`/research-content/${modalType}`, values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchResearchContent();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const renderResearchAreas = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => handleAdd('area')}
        >
          添加研究方向
        </Button>
      </div>
      <Row gutter={[16, 16]}>
        {researchContent.researchAreas.map((area) => (
          <Col xs={24} md={12} lg={8} key={area._id}>
            <Card
              title={area.title}
              extra={
                <Space>
                  <Switch 
                    checked={area.isActive}
                    onChange={async (checked) => {
                      try {
                        await apiService.put(`/research-content/area/${area._id}`, {
                          title: area.title,
                          description: area.description,
                          icon: area.icon,
                          color: area.color,
                          details: area.details,
                          isActive: checked
                        });
                        fetchResearchContent();
                      } catch (error) {
                        message.error('更新状态失败');
                      }
                    }}
                  />
                  <Button 
                    type="link" 
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(area, 'area')}
                  />
                  <Popconfirm
                    title="确定要删除这个研究方向吗？"
                    onConfirm={() => handleDelete(area._id, 'area')}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              }
            >
              <p>{area.description}</p>
              <div style={{ marginTop: 8 }}>
                <strong>研究内容：</strong>
                <ul style={{ marginTop: 4 }}>
                  {area.details.map((detail, index) => (
                    <li key={index}>{detail}</li>
                  ))}
                </ul>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderPublications = () => (
    <div style={{ padding: '0' }}>
      <RepresentativePaperManagement />
    </div>
  );

  const renderEquipment = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => handleAdd('equipment')}
        >
          添加研究设备
        </Button>
      </div>
      <Row gutter={[16, 16]}>
        {researchContent.equipment.map((equipment) => (
          <Col xs={24} md={12} lg={8} key={equipment._id}>
            <Card
              title={equipment.name}
              extra={
                <Space>
                  <Switch 
                    checked={equipment.isActive}
                    onChange={async (checked) => {
                      try {
                        await apiService.put(`/research-content/equipment/${equipment._id}`, {
                          name: equipment.name,
                          description: equipment.description,
                          isActive: checked
                        });
                        fetchResearchContent();
                      } catch (error) {
                        message.error('更新状态失败');
                      }
                    }}
                  />
                  <Button 
                    type="link" 
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(equipment, 'equipment')}
                  />
                  <Popconfirm
                    title="确定要删除这个设备吗？"
                    onConfirm={() => handleDelete(equipment._id, 'equipment')}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              }
            >
              <p>{equipment.description}</p>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderModal = () => {
    const getModalTitle = () => {
      const titles = {
        area: editingItem ? '编辑研究方向' : '添加研究方向',
        equipment: editingItem ? '编辑设备' : '添加设备'
      };
      return titles[modalType];
    };

    const renderForm = () => {
      switch (modalType) {
        case 'area':
          return (
            <>
              <Form.Item
                name="title"
                label="研究方向名称"
                rules={[{ required: true, message: '请输入研究方向名称' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="description"
                label="研究方向描述"
                rules={[{ required: true, message: '请输入研究方向描述' }]}
              >
                <TextArea rows={3} />
              </Form.Item>
              <Form.Item
                name="icon"
                label="图标类名"
                rules={[{ required: true, message: '请输入图标类名' }]}
              >
                <Input placeholder="例如：SunIcon" />
              </Form.Item>
              <Form.Item
                name="color"
                label="渐变色"
                rules={[{ required: true, message: '请输入渐变色' }]}
              >
                <Input placeholder="例如：from-yellow-400 to-orange-500" />
              </Form.Item>
              <Form.Item
                name="details"
                label="研究内容"
                rules={[{ required: true, message: '请输入研究内容' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="每行一个研究内容，例如：&#10;新型钙钛矿材料设计合成&#10;界面工程与器件优化&#10;稳定性机理研究"
                />
              </Form.Item>
              <Form.Item
                name="isActive"
                label="是否启用"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </>
          );



        case 'equipment':
          return (
            <>
              <Form.Item
                name="name"
                label="设备名称"
                rules={[{ required: true, message: '请输入设备名称' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="description"
                label="设备描述"
                rules={[{ required: true, message: '请输入设备描述' }]}
              >
                <TextArea rows={3} />
              </Form.Item>
              <Form.Item
                name="isActive"
                label="是否启用"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <Modal
        title={getModalTitle()}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {renderForm()}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                {editingItem ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>加载中...</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>研究成果管理</Title>
      
      <Tabs 
        defaultActiveKey="areas"
        items={[
          {
            key: 'areas',
            label: '研究方向',
            children: renderResearchAreas()
          },
          {
            key: 'publications',
            label: '代表性论文',
            children: renderPublications()
          },
          {
            key: 'equipment',
            label: '研究设备',
            children: renderEquipment()
          }
        ]}
      />

      {renderModal()}
    </div>
  );
};

export default ResearchManagement; 